/**
 * Every user-facing string goes through here (decision 34: English at launch, all strings through i18n from day one;
 * part 11 log, table T). A catalog maps keys to messages; a message is text with `{name}` placeholders, or plural forms
 * picked by the `count` parameter with the locale's plural rules. Numbers are written in the locale's digits and
 * grouping. Adding a language is a new catalog with the same keys and placeholders.
 */

/** The plural categories of `Intl.PluralRules`; `other` is the one every language has. */
export type PluralCategory = 'zero' | 'one' | 'two' | 'few' | 'many' | 'other';

/** Plural forms of one message, chosen by its `count` parameter (T3). */
export type PluralForms = { readonly other: string } & Partial<Readonly<Record<Exclude<PluralCategory, 'other'>, string>>>;

/** One message: text with `{name}` placeholders (T1), or plural forms of such text (T3). */
export type Message = string | PluralForms;

/** A catalog: every message of one language, by key. */
export type Catalog = Readonly<Record<string, Message>>;

/** The placeholder names in a text, as a union (never when it has none). */
type PlaceholdersIn<S extends string> = S extends `${string}{${infer Name}}${infer Rest}` ? Name | PlaceholdersIn<Rest> : never;

/** The parameter names of a message: its placeholders, plus `count` for plural forms. */
export type ParamNames<M extends Message> = M extends string ? PlaceholdersIn<M> : 'count' | PlaceholdersIn<M[keyof M & PluralCategory] & string>;

/** A parameter's value: text is written as it is, a number in the locale's format. */
export type ParamValue = string | number;

/** The arguments a key takes after the key itself: none, or one object holding every parameter. */
export type ParamsArg<C extends Catalog, K extends keyof C> = [ParamNames<C[K]>] extends [never] ? [] : [Readonly<Record<ParamNames<C[K]>, ParamValue>>];

/** Translates keys of the catalog `C`. */
export type Translate<C extends Catalog> = <K extends keyof C & string>(key: K, ...params: ParamsArg<C, K>) => string;

/** A message asked for with a parameter it doesn't get, or a key no catalog holds (T4): a programming error. */
export class MessageError extends Error {
  override readonly name = 'MessageError';
}

const PLACEHOLDER = /\{([A-Za-z][A-Za-z0-9]*)\}/gu;

/** The placeholder names in `text`, in order of first use (T1, T6). */
export function placeholders(text: string): string[] {
  return [...new Set(Array.from(text.matchAll(PLACEHOLDER), (match) => String(match[1])))];
}

/** Every text of a message: itself, or each of its plural forms. */
export function texts(message: Message): string[] {
  return typeof message === 'string' ? [message] : Object.values(message);
}

/**
 * The translator for `catalog` in `locale` (T1–T5). It throws {@link MessageError} for an unknown key or a missing
 * parameter, which the types already refuse; the tests render every key, so neither reaches the writer.
 */
export function createTranslator<C extends Catalog>(catalog: C, locale: string): Translate<C> {
  const plurals = new Intl.PluralRules(locale);
  const numbers = new Intl.NumberFormat(locale);
  const write = (value: ParamValue): string => (typeof value === 'number' ? numbers.format(value) : value);
  return (key, ...rest) => {
    const message: Message | undefined = Object.hasOwn(catalog, key) ? catalog[key] : undefined;
    if (message === undefined) throw new MessageError(`no message "${key}"`);
    const params: Readonly<Record<string, ParamValue>> = rest[0] ?? {};
    const value = (name: string): ParamValue => {
      const found = Object.hasOwn(params, name) ? params[name] : undefined;
      if (found === undefined) throw new MessageError(`message "${key}" needs the parameter "${name}"`);
      return found;
    };
    let text: string;
    if (typeof message === 'string') text = message;
    else {
      const count = value('count');
      if (typeof count !== 'number') throw new MessageError(`message "${key}" needs a number for "count"`);
      text = message[plurals.select(count)] ?? message.other;
    }
    return text.replace(PLACEHOLDER, (_match, name: string) => write(value(name)));
  };
}
